/**
 * CONTACT THE CALLER, from their row on the Calls screen.
 *
 * Neelum, 2026-10-17: "She needs to be able to call, text and email. She
 * needs the ability to contact them and choose which method, it's possible
 * she will need to switch contact methods occasionally (such as following
 * up via email and then call). This can exist as a box within each
 * caller's shopdesk card." Anthony: "Allow texting in their system the way
 * we have ours setup" -- so a text is the comms panel's reply
 * (/api/sms/thread), from the shop's own line, with what was said above it.
 *
 * The payment buttons sit under the box (Neelum, same evening: "a row of
 * buttons underneath the send agreement button. Each button would send each
 * separate payment method information"). A button puts that method's words
 * into the open text or email so it can be read before Send; nothing sends
 * on its own. The words are the shop's, set by an admin at the top of the
 * Calls screen (payInfoEditor below).
 *
 * THEIR NUMBER AND EMAIL, editable at the top of the box (Anthony,
 * 2026-10-18: "give laura the ability to change a phone number and email
 * ... add a country code but default it to +1 ... let it be changed by
 * selecting and changing. Don't make a shit mobile issue with an off screen
 * radial"). The code is a native select, full width, so a phone opens its
 * own picker. The caller stays on the number they came in on (0227); this
 * is where Call, Text and Email go now. Texts only go to +1.
 */

import { api, db } from "../data/client.ts";
import { esc, since } from "../ui/dom.ts";
import type { PayMethod } from "../domain/paymethods.ts";
import { COUNTRY_CODES, phoneProblem, showNumber } from "../domain/countrycodes.ts";

type Said = { occurred_at: string; kind: string; direction: string; body: string | null; subject: string | null };
type Contact = { email: string; cc: string; phone: string; saved: boolean; said: Said[]; pay: PayMethod[] };

/** Saved on a caller here; the Calls row reads it back so the payment row texts the same number. */
export type Reach = { cc: string; phone: string; email: string };
let onSaved: ((phone10: string, r: Reach) => void) | null = null;
export const whenDetailsSaved = (f: (phone10: string, r: Reach) => void): void => { onSaved = f; };

function detailsHtml(st: Contact): string {
  const opts = COUNTRY_CODES.map((c) => `<option value="${esc(c.code)}"${c.code === st.cc ? " selected" : ""}>${esc(c.label)}</option>`).join("");
  return `<details class="contact-details" style="margin:.4rem 0">
    <summary class="fine" style="cursor:pointer"><b>Their number and email</b> <span class="quiet">${esc(showNumber(st.cc, st.phone))}${st.email ? ` &middot; ${esc(st.email)}` : ""} &middot; change</span></summary>
    <div style="display:grid;gap:.4rem;margin-top:.4rem;max-width:100%">
      <label class="field" style="min-width:0"><span>Country code</span>
        <select data-cc style="display:block;width:100%;max-width:100%">${opts}</select></label>
      <label class="field" style="min-width:0"><span>Phone number</span>
        <input type="tel" inputmode="tel" autocomplete="off" data-phone value="${esc(st.cc === "1" && st.phone.length === 10 ? showNumber("1", st.phone) : st.phone)}" style="width:100%"></label>
      <label class="field" style="min-width:0"><span>Email</span>
        <input type="email" inputmode="email" autocomplete="off" data-email value="${esc(st.email)}" placeholder="their@email.com" style="width:100%"></label>
      <div class="row" style="gap:.4rem;align-items:center;flex-wrap:wrap"><button type="button" class="btn primary sm" data-save-details>Save</button><span class="fine" data-details-msg></span></div>
    </div>
  </details>`;
}

async function bearer(): Promise<string> {
  const sb = await db();
  const { data: { session } } = await sb.auth.getSession();
  return session?.access_token ?? "";
}

async function call<T>(path: string, payload?: unknown): Promise<T> {
  const r = await fetch(api(path), {
    method: payload ? "POST" : "GET",
    headers: { Authorization: `Bearer ${await bearer()}`, ...(payload ? { "Content-Type": "application/json" } : {}) },
    body: payload ? JSON.stringify(payload) : undefined,
  });
  const out = await r.json().catch(() => ({})) as T & { error?: string };
  if (!r.ok) throw new Error(out.error ?? "That did not go through. Try again.");
  return out;
}

const phoneFmt = (p: string): string => `(${p.slice(0, 3)}) ${p.slice(3, 6)}-${p.slice(6)}`;

function saidHtml(s: Said[]): string {
  if (!s.length) return `<div class="fine quiet">No texts or emails with them yet.</div>`;
  return `<div class="contact-said" style="max-height:14rem;overflow-y:auto;display:grid;gap:.35rem;margin:.4rem 0">${s.map((m) => {
    const out = m.direction === "outbound";
    return `<div class="fine" style="padding:.35rem .5rem;border-radius:.4rem;background:var(--${out ? "panel-2" : "panel"}, #f3f3f3);${out ? "margin-left:1.5rem" : "margin-right:1.5rem"}">
      <b>${out ? "Us" : "Them"} &middot; ${m.kind === "email" ? "email" : "text"}</b> <span class="quiet">${esc(since(m.occurred_at))}</span>
      ${m.subject ? `<div><i>${esc(m.subject)}</i></div>` : ""}<div style="white-space:pre-wrap">${esc(m.body ?? "")}</div></div>`;
  }).join("")}</div>`;
}

/** Open the contact box for one caller in `host`. */
export async function mountContact(host: HTMLElement, phone10: string, name: string): Promise<void> {
  host.hidden = false;
  host.innerHTML = `<div class="fine quiet">Opening&hellip;</div>`;
  let st: Contact;
  try { st = await call<Contact>(`/api/calldesk/contact?phone10=${encodeURIComponent(phone10)}`); }
  catch (e) { host.innerHTML = `<p class="msg bad">${esc(e instanceof Error ? e.message : String(e))}</p>`; return; }
  let how: "call" | "text" | "email" = st.cc === "1" ? "text" : "call";
  const who = name || phoneFmt(phone10);
  const abroad = () => st.cc !== "1";
  const dial = () => `+${st.cc}${st.phone}`;
  let detailsOpen = false;

  const draw = (): void => {
    const tab = (k: typeof how, label: string) =>
      `<button type="button" class="btn sm ${how === k ? "primary" : "ghost"}" data-how="${k}" aria-pressed="${how === k}">${label}</button>`;
    const body = how === "call"
      ? `<div class="row" style="margin:.5rem 0"><a class="btn primary" href="tel:${esc(dial())}">Call ${esc(showNumber(st.cc, st.phone))}</a></div>
         <div class="fine quiet">Rings them from this phone.</div>`
      : how === "text" && abroad()
      ? `<div class="fine" style="margin:.5rem 0">Texts only go to +1 numbers. Their number is ${esc(showNumber(st.cc, st.phone))}, so call or email them.</div>`
      : how === "text"
      ? `${saidHtml(st.said)}
         <label class="field"><span>Text ${esc(who)}</span><textarea rows="3" data-say style="width:100%"></textarea></label>
         <div class="row" style="gap:.4rem;align-items:center"><button type="button" class="btn primary sm" data-send="text">Send text</button><span class="fine" data-msg></span></div>`
      : `${saidHtml(st.said)}
         <label class="field"><span>Email</span><input type="email" data-to value="${esc(st.email)}" placeholder="their@email.com"></label>
         <label class="field"><span>Subject</span><input type="text" data-subject></label>
         <label class="field"><span>Message</span><textarea rows="4" data-say style="width:100%"></textarea></label>
         <div class="row" style="gap:.4rem;align-items:center"><button type="button" class="btn primary sm" data-send="email">Send email</button><span class="fine" data-msg></span></div>`;
    const pay = `<div class="fine" style="margin-top:.6rem"><b>Payment info</b> <span class="quiet">puts it in the ${how === "email" ? "email" : "text"} to check, then Send</span></div>
      <div class="row" style="gap:.35rem;flex-wrap:wrap">${st.pay.map((m) =>
        `<button type="button" class="btn ghost sm" data-pay="${esc(m.key)}"${m.text ? "" : ` title="Not set up yet"`}>${esc(m.label)}</button>`).join("")}</div>`;
    host.innerHTML = `<div class="card-head">Contact ${esc(who)}</div>
      ${detailsHtml(st)}
      <div class="row" style="gap:.35rem;flex-wrap:wrap">${tab("call", "Call")}${tab("text", "Text")}${tab("email", "Email")}</div>
      ${body}${pay}`;
    const det = host.querySelector<HTMLDetailsElement>("details.contact-details");
    if (det) { det.open = detailsOpen; det.addEventListener("toggle", () => { detailsOpen = det.open; }); }
    const scroll = host.querySelector<HTMLElement>(".contact-said");
    if (scroll) scroll.scrollTop = scroll.scrollHeight;
  };
  draw();

  host.onclick = async (e) => {
    const t = e.target as HTMLElement;
    const msg = (s: string, bad = false) => { const m = host.querySelector<HTMLElement>("[data-msg]"); if (m) { m.textContent = s; m.className = `fine${bad ? " bad" : ""}`; } };
    const h = t.closest<HTMLButtonElement>("button[data-how]");
    if (h) { how = h.dataset["how"] as typeof how; draw(); return; }

    /* SAVE THEIR NUMBER AND EMAIL. */
    const sd = t.closest<HTMLButtonElement>("button[data-save-details]");
    if (sd) {
      const dm = host.querySelector<HTMLElement>("[data-details-msg]");
      const say = (s: string, bad = false) => { if (dm) { dm.textContent = s; dm.className = `fine${bad ? " bad" : ""}`; } };
      const cc = host.querySelector<HTMLSelectElement>("select[data-cc]")?.value ?? "1";
      let digits = (host.querySelector<HTMLInputElement>("input[data-phone]")?.value ?? "").replace(/\D/g, "");
      if (cc === "1" && digits.length === 11 && digits.startsWith("1")) digits = digits.slice(1);
      const email = host.querySelector<HTMLInputElement>("input[data-email]")?.value.trim() ?? "";
      const bad = phoneProblem(cc, digits);
      if (bad) { say(bad, true); return; }
      sd.disabled = true; say("Saving…");
      try {
        const out = await call<{ cc: string; phone: string; email: string }>("/api/calldesk/details", { phone10, cc, phone: digits, email });
        st.cc = out.cc; st.phone = out.phone; st.email = out.email; st.saved = true;
        if (abroad() && how === "text") how = "call";
        onSaved?.(phone10, { cc: st.cc, phone: st.phone, email: st.email });
        detailsOpen = false; draw(); msg("Saved their number and email.");
      } catch (err) { sd.disabled = false; say(err instanceof Error ? err.message : String(err), true); }
      return;
    }

    const p = t.closest<HTMLButtonElement>("button[data-pay]");
    if (p) {
      const m = st.pay.find((x) => x.key === p.dataset["pay"]);
      if (!m?.text) { alert(`${m?.label ?? "That"} is not set up yet. An admin adds it under Payment info at the top of the Calls screen.`); return; }
      if (how === "call" || (how === "text" && abroad())) { how = abroad() ? "email" : "text"; draw(); }
      const ta = host.querySelector<HTMLTextAreaElement>("textarea[data-say]");
      if (ta) { ta.value = ta.value.trim() ? `${ta.value.trim()}\n\n${m.text}` : m.text; ta.focus(); }
      const subj = host.querySelector<HTMLInputElement>("input[data-subject]");
      if (subj && !subj.value.trim()) subj.value = `Payment by ${m.label}`;
      return;
    }

    const s = t.closest<HTMLButtonElement>("button[data-send]");
    if (!s) return;
    const say = host.querySelector<HTMLTextAreaElement>("textarea[data-say]")?.value.trim() ?? "";
    if (!say) { msg("Type the message first.", true); return; }
    s.disabled = true; msg("Sending…");
    try {
      if (s.dataset["send"] === "text") {
        const out = await call<{ ok?: boolean; warning?: string }>("/api/sms/thread", { phone: st.phone, via: phone10, message: say });
        st.said.push({ occurred_at: new Date().toISOString(), kind: "sms", direction: "outbound", body: say, subject: null });
        draw(); msg(out.warning ?? "Sent.");
      } else {
        const to = host.querySelector<HTMLInputElement>("input[data-to]")?.value.trim() ?? "";
        const subject = host.querySelector<HTMLInputElement>("input[data-subject]")?.value.trim() ?? "";
        const out = await call<{ ok?: boolean; warning?: string }>("/api/calldesk/email", { phone10, to, subject, message: say });
        st.email = to;
        st.said.push({ occurred_at: new Date().toISOString(), kind: "email", direction: "outbound", body: say, subject });
        draw(); msg(out.warning ?? "Sent.");
      }
    } catch (err) { s.disabled = false; msg(err instanceof Error ? err.message : String(err), true); }
  };
}

/**
 * PAYMENT INFO, set by an admin, at the top of the Calls screen: the words
 * each payment button puts in a message. Shown read-only to everyone else.
 */
export async function payInfoEditor(host: HTMLElement): Promise<void> {
  let st: { pay: PayMethod[]; may_edit: boolean };
  try { st = await call("/api/calldesk/payinfo"); } catch { host.hidden = true; return; }
  if (!st.may_edit) { host.hidden = true; return; }
  host.hidden = false;
  const empty = st.pay.every((m) => !m.text);
  host.innerHTML = `<details class="card pad"${empty ? " open" : ""}>
    <summary class="card-head" style="cursor:pointer">Payment info${empty ? " (not set up yet)" : ""}</summary>
    <p class="fine quiet">What each payment button on a caller puts in the text or email. Type it exactly as the client should read it. Only admins see this box.</p>
    ${st.pay.map((m) => `<label class="field"><span>${esc(m.label)}</span>
      <textarea rows="3" style="width:100%" data-pay-edit="${esc(m.key)}">${esc(m.text)}</textarea></label>`).join("")}
    <div class="row" style="gap:.4rem;align-items:center"><button type="button" class="btn primary sm" id="pay-save">Save payment info</button><span class="fine" id="pay-msg"></span></div>
  </details>`;
  host.querySelector<HTMLButtonElement>("#pay-save")?.addEventListener("click", async (e) => {
    const b = e.currentTarget as HTMLButtonElement;
    const m = host.querySelector<HTMLElement>("#pay-msg");
    const methods: Record<string, string> = {};
    host.querySelectorAll<HTMLTextAreaElement>("textarea[data-pay-edit]").forEach((t) => { methods[t.dataset["payEdit"]!] = t.value; });
    b.disabled = true; if (m) m.textContent = "Saving…";
    try { await call("/api/calldesk/payinfo", { methods }); if (m) m.textContent = "Saved."; }
    catch (err) { if (m) m.textContent = err instanceof Error ? err.message : String(err); }
    b.disabled = false;
  });
}
